import { useEffect, useState } from 'react'
import { Smartphone, X, ArrowRight } from 'lucide-react'
import { AppleLogo } from './AppleLogo'
import {
  ANDROID_URL,
  IOS_APPSTORE_URL,
  detectPlatform,
  trackDownload,
  type DevicePlatform,
} from '../lib/appLinks'

/**
 * AppDownloadBanner — the "download suggestion the moment the site opens".
 * A slim, dismissible bar that slides in under the header and adapts to the
 * visitor's device now that both apps are LIVE on the public stores:
 *   • Android → one-tap "Download on Google Play"
 *   • iOS     → one-tap "Download on the App Store"
 *   • Desktop → "Get it on your phone" (Play default)
 * Dismissal is remembered (localStorage) so it doesn't nag on every visit.
 * Fires a download-funnel beacon on view + on click.
 */
const DISMISS_KEY = 'bu:getapp:dismissed:v1'

function copyFor(platform: DevicePlatform): {
  msg: string
  cta: string
  href: string
  icon: JSX.Element
  action: 'android' | 'ios'
} {
  if (platform === 'ios') {
    return {
      msg: '🎉 BimaUncle is now live on the App Store.',
      cta: 'Download',
      href: IOS_APPSTORE_URL,
      icon: <AppleLogo size={15} />,
      action: 'ios',
    }
  }
  // Android + desktop both point at the live Play listing.
  return {
    msg:
      platform === 'android'
        ? '🎉 BimaUncle is now live on Google Play.'
        : '🎉 BimaUncle is live on Google Play — get it on your phone.',
    cta: 'Download',
    href: ANDROID_URL,
    icon: <Smartphone size={17} />,
    action: 'android',
  }
}

export function AppDownloadBanner() {
  const [show, setShow] = useState(false)
  const [platform, setPlatform] = useState<DevicePlatform>('desktop')

  useEffect(() => {
    let dismissed = false
    try {
      dismissed = localStorage.getItem(DISMISS_KEY) === '1'
    } catch {
      /* private mode — treat as not dismissed */
    }
    if (dismissed) return
    setPlatform(detectPlatform())
    // Slide in on the next frame so the transition actually animates.
    const raf = requestAnimationFrame(() => setShow(true))
    trackDownload('view')
    return () => cancelAnimationFrame(raf)
  }, [])

  const dismiss = () => {
    setShow(false)
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      /* ignore */
    }
  }

  if (!show) return null
  const c = copyFor(platform)

  return (
    <div
      role="region"
      aria-label="Get the app"
      className="sticky top-16 z-30 animate-fade-up border-b border-[var(--color-divider)] bg-gradient-to-r from-[#0052CC] to-[#5856D6] text-white shadow-sm"
    >
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2.5">
        <span className="hidden shrink-0 sm:inline-flex">{c.icon}</span>
        <p className="flex-1 text-sm font-medium leading-snug">{c.msg}</p>
        <a
          href={c.href}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackDownload(c.action)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-pill bg-white px-3.5 py-1.5 text-sm font-semibold text-brand transition-transform hover:-translate-y-0.5"
        >
          {c.cta}
          <ArrowRight size={15} />
        </a>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="shrink-0 rounded-full p-1 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
        >
          <X size={17} />
        </button>
      </div>
    </div>
  )
}
